import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { Calendar as CalendarIcon, CalendarCog } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

export const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** Whether `date` falls outside [today, maxDate] by calendar day, so the time of day never counts. */
export const outsideDayRange = (date: Date, today: Date, maxDate: Date) => {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return (
    day < new Date(today.getFullYear(), today.getMonth(), today.getDate()) ||
    day > new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())
  );
};

interface PaymentDatePickerProps {
  value: Date | undefined;
  onChange: (date: Date) => void;
  /** Latest day the customer may pick; today is always the earliest. */
  maxDate: Date;
  /** False locks the payment to today and says why in a tooltip instead of opening a calendar. */
  canDefer: boolean;
  /** What the lock says: differs between a full payment and a part payment. */
  lockedCopyKey: string;
  disabled?: boolean;
  /** Date text beside the button; absent for the button-only variant, where the page says the date itself. */
  label?: string;
  /** 'plan' puts a cog on the button and names the field for tests; 'compact' is the bare button beside an input. */
  variant?: 'pay' | 'plan' | 'compact';
  'data-testid'?: string;
}

/** The date a payment is taken, as a labelled pill with the calendar button at its end. */
export function PaymentDatePicker({
  value,
  onChange,
  maxDate,
  canDefer,
  lockedCopyKey,
  disabled,
  label,
  variant = 'pay',
  ...rest
}: PaymentDatePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const today = new Date();
  const Icon = variant === 'plan' ? CalendarCog : CalendarIcon;
  const control = canDefer ? (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="rounded-md"
          disabled={disabled}
          aria-label={t('paymentTabs.selectDate')}
          data-testid={rest['data-testid']}
        >
          <Icon className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0 rounded-lg" align="end" side="bottom">
        <Calendar
          mode="single"
          selected={value}
          captionLayout="dropdown-months"
          startMonth={today}
          endMonth={maxDate}
          disabled={(date) => outsideDayRange(date, today, maxDate)}
          onSelect={(date) => {
            if (!date) return;
            onChange(date);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A span, because a disabled button fires no pointer events for the tooltip to catch. */}
        <span className="inline-flex size-9 items-center justify-center rounded-md border bg-muted/60 text-muted-foreground cursor-default">
          <Icon className="size-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p>{t(lockedCopyKey)}</p>
      </TooltipContent>
    </Tooltip>
  );

  if (variant === 'compact') return control;
  return (
    <div
      className="flex-1 justify-between flex items-center gap-2 text-sm pl-4 pr-1 py-1 bg-muted/75 rounded-lg"
      data-testid={variant === 'plan' ? 'plan-start-date' : undefined}
    >
      <span className="font-medium">{label}</span>
      {control}
    </div>
  );
}
